import React from 'react';
import { Box, Grid, Typography, Table, TableBody, TableCell, TableHead, TableRow, TableContainer, LinearProgress, Stack, Avatar, Skeleton } from '@mui/material';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, LineChart, Line,
  PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend, ComposedChart,
} from 'recharts';
import {
  Payments as PaymentsIcon, Event as EventIcon, People as PeopleIcon, MonetizationOn as MoneyIcon,
  TrendingUp as TrendIcon, Warning as WarningIcon, AssignmentTurnedIn as DoneIcon,
  Inventory as InventoryIcon, Receipt as ReceiptIcon, LocalAtm as AtmIcon,
} from '@mui/icons-material';
import { KpiCard, ChartCard, SectionCard, EmptyState, CHART_COLORS } from './components';
import { formatCurrency, formatNumber, formatPct, formatShortDate } from './types';

const tooltipStyle: any = {
  contentStyle: { borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 },
  labelStyle: { color: '#0f172a', fontWeight: 600 },
};

const fmtSeriesLabel = (d: string) => formatShortDate(d);

// ===================================================================
// 1. OVERVIEW
// ===================================================================
export const OverviewSection: React.FC<{ data: any; loading?: boolean }> = ({ data, loading }) => {
  const k = data?.kpis || {};
  const series = data?.series || [];
  const cmp = data?.comparison;

  return (
    <Stack spacing={2}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard title="Receita realizada" value={formatCurrency(k.total_revenue || 0)} subtitle={`${k.paid_count || 0} pagamentos`}
            color="#14b8a6" icon={<MoneyIcon fontSize="small" />} loading={loading}
            trendPct={cmp?.revenue_change_pct ?? null} size="lg" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard title="Atendimentos" value={formatNumber(k.total_appointments || 0)} subtitle={`${formatNumber(k.completed_appointments || 0)} concluídos · ${formatNumber(k.no_shows || 0)} faltas`}
            color="#8b5cf6" icon={<EventIcon fontSize="small" />} loading={loading}
            trendPct={cmp?.appointments_change_pct ?? null} size="lg" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard title="Ticket médio" value={formatCurrency(k.avg_ticket || 0)} subtitle="Por pagamento realizado"
            color="#0ea5e9" icon={<TrendIcon fontSize="small" />} loading={loading} size="lg" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard title="Taxa de conclusão" value={formatPct(k.completion_rate_pct ?? 0)} subtitle={`No-show ${formatPct(k.no_show_rate_pct ?? 0)}`}
            color="#22c55e" icon={<DoneIcon fontSize="small" />} loading={loading} size="lg" />
        </Grid>

        <Grid item xs={6} sm={3} md={2}>
          <KpiCard title="Pendente" value={formatCurrency(k.total_pending || 0)} color="#f59e0b" icon={<PaymentsIcon fontSize="small" />} loading={loading} />
        </Grid>
        <Grid item xs={6} sm={3} md={2}>
          <KpiCard title="Vencido" value={formatCurrency(k.total_overdue || 0)} color="#ef4444" icon={<WarningIcon fontSize="small" />} loading={loading} />
        </Grid>
        <Grid item xs={6} sm={3} md={2}>
          <KpiCard title="Pacientes" value={formatNumber(k.patients_total || 0)} color="#3b82f6" icon={<PeopleIcon fontSize="small" />} loading={loading} />
        </Grid>
        <Grid item xs={6} sm={3} md={2}>
          <KpiCard title="Novos" value={formatNumber(k.new_patients || 0)} subtitle="No período" color="#06b6d4" icon={<PeopleIcon fontSize="small" />} loading={loading} />
        </Grid>
        <Grid item xs={6} sm={3} md={2}>
          <KpiCard title="Faltas" value={formatNumber(k.no_shows || 0)} color="#f43f5e" icon={<WarningIcon fontSize="small" />} loading={loading} />
        </Grid>
        <Grid item xs={6} sm={3} md={2}>
          <KpiCard title="Estoque baixo" value={formatNumber(k.low_stock_items || 0)} color="#a855f7" icon={<InventoryIcon fontSize="small" />} loading={loading} />
        </Grid>
      </Grid>

      <ChartCard title="Receita e atendimentos por dia" subtitle="Receita realizada (paid_date) x agendamentos no período" height={320}>
        {series.length === 0 ? <EmptyState /> : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={series} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" tickFormatter={fmtSeriesLabel} tick={{ fontSize: 11, fill: '#64748b' }} />
              <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#64748b' }} />
              <RTooltip {...tooltipStyle} formatter={(v: any, name: any) => name === 'Receita' ? formatCurrency(v) : v} labelFormatter={fmtSeriesLabel} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area yAxisId="left" type="monotone" dataKey="revenue" name="Receita" stroke="#14b8a6" fill="#14b8a624" strokeWidth={2} />
              <Bar yAxisId="right" dataKey="appointments" name="Atendimentos" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </ChartCard>
    </Stack>
  );
};

// ===================================================================
// 2. FINANCEIRO
// ===================================================================
export const FinancialSection: React.FC<{ data: any; loading?: boolean }> = ({ data, loading }) => {
  const s = data?.summary || {};
  const aging = data?.aging || {};
  const byMethod = data?.by_payment_method || [];
  const byCategory = data?.by_category || [];
  const series = data?.series || [];
  const top = data?.top_debtors || [];
  const cmp = data?.comparison;

  return (
    <Stack spacing={2}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard title="Receita realizada" value={formatCurrency(s.total_revenue || 0)} subtitle={`${formatNumber(s.paid_count || 0)} pagamentos`} color="#14b8a6" icon={<MoneyIcon fontSize="small" />} trendPct={cmp?.revenue_change_pct ?? null} size="lg" loading={loading} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard title="Faturado no período" value={formatCurrency(s.total_charged || 0)} subtitle="Cobranças emitidas" color="#0ea5e9" icon={<ReceiptIcon fontSize="small" />} size="lg" loading={loading} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard title="Despesas" value={formatCurrency(s.total_expenses || 0)} color="#ef4444" icon={<AtmIcon fontSize="small" />} size="lg" loading={loading} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard title="Resultado líquido" value={formatCurrency(s.net || 0)} subtitle={`Ticket médio ${formatCurrency(s.avg_ticket || 0)}`} color={Number(s.net || 0) >= 0 ? '#22c55e' : '#ef4444'} icon={<TrendIcon fontSize="small" />} size="lg" loading={loading} />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <ChartCard title="Evolução diária" subtitle="Receita realizada · cobranças emitidas · despesas">
            {series.length === 0 ? <EmptyState /> : (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={series} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tickFormatter={fmtSeriesLabel} tick={{ fontSize: 11, fill: '#64748b' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`} />
                  <RTooltip {...tooltipStyle} formatter={(v: any) => formatCurrency(v)} labelFormatter={fmtSeriesLabel} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Area type="monotone" dataKey="revenue" name="Receita" stroke="#14b8a6" fill="#14b8a624" strokeWidth={2} />
                  <Line type="monotone" dataKey="charges" name="Cobranças" stroke="#0ea5e9" strokeWidth={2} dot={false} />
                  <Bar dataKey="expenses" name="Despesas" fill="#ef4444" radius={[4, 4, 0, 0]} />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <ChartCard title="Aging — A receber" subtitle="Distribuição por faixa de atraso">
            {!aging || (aging.total ?? 0) === 0 ? <EmptyState title="Nada em aberto" /> : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={[
                      { name: 'A vencer', value: aging.a_vencer || 0 },
                      { name: '1-30 dias', value: aging.d1_30 || 0 },
                      { name: '31-60 dias', value: aging.d31_60 || 0 },
                      { name: '61-90 dias', value: aging.d61_90 || 0 },
                      { name: '+90 dias', value: aging.d90_plus || 0 },
                    ]}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={50}
                    outerRadius={90}
                    paddingAngle={3}
                  >
                    {[ '#22c55e', '#facc15', '#fb923c', '#f97316', '#ef4444' ].map((c, i) => <Cell key={i} fill={c} />)}
                  </Pie>
                  <RTooltip {...tooltipStyle} formatter={(v: any) => formatCurrency(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <ChartCard title="Receita por método" subtitle="Pagamentos realizados no período" height={300}>
            {byMethod.length === 0 ? <EmptyState /> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={byMethod} layout="vertical" margin={{ top: 10, right: 20, left: 60, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`} />
                  <YAxis type="category" dataKey="method" tick={{ fontSize: 11, fill: '#64748b' }} width={140} />
                  <RTooltip {...tooltipStyle} formatter={(v: any) => formatCurrency(v)} />
                  <Bar dataKey="amount" name="Valor" fill="#14b8a6" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <ChartCard title="Por categoria" height={300}>
            {byCategory.length === 0 ? <EmptyState /> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={byCategory.slice(0, 12)} margin={{ top: 10, right: 10, left: 0, bottom: 50 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="category" tick={{ fontSize: 10, fill: '#64748b' }} angle={-30} textAnchor="end" interval={0} height={60} />
                  <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`} />
                  <RTooltip {...tooltipStyle} formatter={(v: any) => formatCurrency(v)} />
                  <Bar dataKey="amount" name="Valor" fill="#0ea5e9" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Grid>
      </Grid>

      <SectionCard title="Top devedores" subtitle="Maiores valores em aberto (pendentes/vencidos)">
        {top.length === 0 ? <EmptyState title="Sem inadimplência ativa" /> : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Paciente</TableCell>
                  <TableCell align="right">Cobranças</TableCell>
                  <TableCell align="right">Total devido</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {top.map((r: any) => (
                  <TableRow key={r.patient_id} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar sx={{ width: 28, height: 28, bgcolor: '#14b8a622', color: '#0f766e', fontSize: 13, fontWeight: 700 }}>
                          {String(r.patient_name || '?').charAt(0).toUpperCase()}
                        </Avatar>
                        <Typography variant="body2">{r.patient_name}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="right">{formatNumber(r.count)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: '#b91c1c' }}>{formatCurrency(r.total)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </SectionCard>
    </Stack>
  );
};

// ===================================================================
// 3. CASH FLOW
// ===================================================================
export const CashflowSection: React.FC<{ data: any; loading?: boolean }> = ({ data, loading }) => {
  const s = data?.summary || {};
  const series = data?.series || [];
  return (
    <Stack spacing={2}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard title="Entradas" value={formatCurrency(s.total_inflow || 0)} color="#22c55e" icon={<MoneyIcon fontSize="small" />} size="lg" loading={loading} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard title="Saídas" value={formatCurrency(s.total_outflow || 0)} color="#ef4444" icon={<AtmIcon fontSize="small" />} size="lg" loading={loading} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard title="Resultado" value={formatCurrency(s.net || 0)} color={Number(s.net || 0) >= 0 ? '#0f766e' : '#ef4444'} icon={<TrendIcon fontSize="small" />} size="lg" loading={loading} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard title="Saldo final" value={formatCurrency(s.ending_balance || 0)} subtitle="Saldo acumulado no período" color="#0ea5e9" icon={<TrendIcon fontSize="small" />} size="lg" loading={loading} />
        </Grid>
      </Grid>
      <ChartCard title="Fluxo de caixa diário" subtitle="Entradas, saídas e saldo acumulado" height={360}>
        {series.length === 0 ? <EmptyState /> : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={series} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" tickFormatter={fmtSeriesLabel} tick={{ fontSize: 11, fill: '#64748b' }} />
              <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`} />
              <RTooltip {...tooltipStyle} formatter={(v: any) => formatCurrency(v)} labelFormatter={fmtSeriesLabel} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="inflow" name="Entradas" fill="#22c55e" radius={[4, 4, 0, 0]} />
              <Bar dataKey="outflow" name="Saídas" fill="#ef4444" radius={[4, 4, 0, 0]} />
              <Line type="monotone" dataKey="balance" name="Saldo" stroke="#0f766e" strokeWidth={2.5} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </ChartCard>
    </Stack>
  );
};

// ===================================================================
// 4. RECEIVABLES (aging detalhado + próximos vencimentos)
// ===================================================================
export const ReceivablesSection: React.FC<{ data: any; loading?: boolean }> = ({ data, loading }) => {
  const a = data?.aging || {};
  const u = data?.upcoming || {};
  const top = data?.top_debtors || [];
  const total = a.total || 0;
  const seg = (label: string, val: number, color: string) => ({ label, val, color, pct: total ? (val / total) * 100 : 0 });
  const segs = [
    seg('A vencer', a.a_vencer || 0, '#22c55e'),
    seg('1-30 dias', a.d1_30 || 0, '#facc15'),
    seg('31-60', a.d31_60 || 0, '#fb923c'),
    seg('61-90', a.d61_90 || 0, '#f97316'),
    seg('+90', a.d90_plus || 0, '#ef4444'),
  ];

  return (
    <Stack spacing={2}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard title="Em aberto" value={formatCurrency(data?.open_total || 0)} subtitle={`${formatNumber(data?.open_count || 0)} cobranças`} color="#f59e0b" icon={<PaymentsIcon fontSize="small" />} size="lg" loading={loading} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard title="A vencer 30 dias" value={formatCurrency(u.due_30 || 0)} color="#0ea5e9" icon={<MoneyIcon fontSize="small" />} loading={loading} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard title="A vencer 60 dias" value={formatCurrency(u.due_60 || 0)} color="#0ea5e9" icon={<MoneyIcon fontSize="small" />} loading={loading} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard title="A vencer 90 dias" value={formatCurrency(u.due_90 || 0)} color="#0ea5e9" icon={<MoneyIcon fontSize="small" />} loading={loading} />
        </Grid>
      </Grid>

      <SectionCard title="Distribuição por aging" subtitle={`Total em aberto: ${formatCurrency(total)}`}>
        <Stack spacing={1.5}>
          {segs.map(s => (
            <Box key={s.label}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#475569' }}>{s.label}</Typography>
                <Typography variant="caption" sx={{ fontWeight: 700, color: s.color }}>{formatCurrency(s.val)} <span style={{ color: '#94a3b8', fontWeight: 500 }}>· {s.pct.toFixed(1)}%</span></Typography>
              </Box>
              <LinearProgress variant="determinate" value={Math.min(100, s.pct)} sx={{ height: 8, borderRadius: 6, bgcolor: '#f1f5f9', '& .MuiLinearProgress-bar': { bgcolor: s.color, borderRadius: 6 } }} />
            </Box>
          ))}
        </Stack>
      </SectionCard>

      <SectionCard title="Devedores em aberto" subtitle="Ordenado por valor devido">
        {top.length === 0 ? <EmptyState title="Sem inadimplência" /> : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Paciente</TableCell>
                  <TableCell align="right">Cobranças</TableCell>
                  <TableCell align="right">Total devido</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {top.map((r: any) => (
                  <TableRow key={r.patient_id} hover>
                    <TableCell>{r.patient_name}</TableCell>
                    <TableCell align="right">{formatNumber(r.count)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: '#b91c1c' }}>{formatCurrency(r.total)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </SectionCard>
    </Stack>
  );
};

// ===================================================================
// 5. APPOINTMENTS
// ===================================================================
export const AppointmentsSection: React.FC<{ data: any; loading?: boolean }> = ({ data, loading }) => {
  const s = data?.summary || {};
  const byStatus = data?.by_status || [];
  const byType = data?.by_type || [];
  const byDay = data?.by_day_of_week || [];
  const byHour = data?.by_hour || [];
  const byProf = data?.by_professional || [];
  const series = data?.series || [];

  return (
    <Stack spacing={2}>
      <Grid container spacing={2}>
        <Grid item xs={6} sm={3}><KpiCard title="Total" value={formatNumber(s.total || 0)} color="#0f766e" icon={<EventIcon fontSize="small" />} size="lg" loading={loading} /></Grid>
        <Grid item xs={6} sm={3}><KpiCard title="Concluídos" value={formatNumber(s.completed || 0)} subtitle={formatPct(s.completion_rate)} color="#22c55e" icon={<DoneIcon fontSize="small" />} size="lg" loading={loading} /></Grid>
        <Grid item xs={6} sm={3}><KpiCard title="Faltas" value={formatNumber(s.no_show || 0)} subtitle={formatPct(s.no_show_rate)} color="#ef4444" icon={<WarningIcon fontSize="small" />} size="lg" loading={loading} /></Grid>
        <Grid item xs={6} sm={3}><KpiCard title="Cancelados" value={formatNumber(s.cancelled || 0)} subtitle={formatPct(s.cancellation_rate)} color="#f59e0b" icon={<WarningIcon fontSize="small" />} size="lg" loading={loading} /></Grid>
      </Grid>

      <ChartCard title="Volume diário" subtitle="Total · concluídos · faltas">
        {series.length === 0 ? <EmptyState /> : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={series} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" tickFormatter={fmtSeriesLabel} tick={{ fontSize: 11, fill: '#64748b' }} />
              <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
              <RTooltip {...tooltipStyle} labelFormatter={fmtSeriesLabel} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="total" name="Total" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
              <Line type="monotone" dataKey="completed" name="Concluídos" stroke="#22c55e" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="no_show" name="Faltas" stroke="#ef4444" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <ChartCard title="Por status" height={300}>
            {byStatus.length === 0 ? <EmptyState /> : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={byStatus} dataKey="count" nameKey="status_label" innerRadius={50} outerRadius={95} paddingAngle={3} label={(e: any) => `${e.status_label} (${e.count})`} labelLine={false}>
                    {byStatus.map((_: any, i: number) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <RTooltip {...tooltipStyle} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <ChartCard title="Por dia da semana" height={300}>
            {byDay.length === 0 ? <EmptyState /> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={byDay} margin={{ top: 10, right: 10, left: 0, bottom: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="day" tick={{ fontSize: 10, fill: '#64748b' }} angle={-25} textAnchor="end" />
                  <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
                  <RTooltip {...tooltipStyle} />
                  <Bar dataKey="count" name="Atendimentos" fill="#0f766e" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartCard title="Por horário" height={300}>
            {byHour.length === 0 ? <EmptyState /> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={byHour} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="hour" tick={{ fontSize: 11, fill: '#64748b' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
                  <RTooltip {...tooltipStyle} />
                  <Bar dataKey="count" name="Atendimentos" fill="#3b82f6" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <ChartCard title="Por tipo" height={300}>
            {byType.length === 0 ? <EmptyState /> : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={byType} dataKey="count" nameKey="type" outerRadius={95} label labelLine={false}>
                    {byType.map((_: any, i: number) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <RTooltip {...tooltipStyle} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Grid>
      </Grid>

      <SectionCard title="Por profissional" subtitle="Ordenado por volume">
        {byProf.length === 0 ? <EmptyState /> : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Profissional</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right">Concluídos</TableCell>
                  <TableCell align="right">Faltas</TableCell>
                  <TableCell align="right">Cancelados</TableCell>
                  <TableCell align="right">Conclusão</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {byProf.map((p: any) => (
                  <TableRow key={p.professional_id} hover>
                    <TableCell>{p.professional_name}</TableCell>
                    <TableCell align="right">{formatNumber(p.total)}</TableCell>
                    <TableCell align="right">{formatNumber(p.completed)}</TableCell>
                    <TableCell align="right">{formatNumber(p.no_show)}</TableCell>
                    <TableCell align="right">{formatNumber(p.cancelled)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: '#15803d' }}>{formatPct(p.completion_rate)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </SectionCard>
    </Stack>
  );
};

// ===================================================================
// 6. PRODUCTIVITY
// ===================================================================
export const ProductivitySection: React.FC<{ data: any; loading?: boolean }> = ({ data, loading }) => {
  const totals = data?.totals || {};
  const list = data?.professionals || [];

  return (
    <Stack spacing={2}>
      <Grid container spacing={2}>
        <Grid item xs={6} sm={3}><KpiCard title="Receita total" value={formatCurrency(totals.revenue || 0)} color="#14b8a6" icon={<MoneyIcon fontSize="small" />} size="lg" loading={loading} /></Grid>
        <Grid item xs={6} sm={3}><KpiCard title="Atendimentos" value={formatNumber(totals.total_appointments || 0)} subtitle={`${formatNumber(totals.completed_appointments || 0)} concluídos`} color="#8b5cf6" icon={<EventIcon fontSize="small" />} size="lg" loading={loading} /></Grid>
        <Grid item xs={6} sm={3}><KpiCard title="Ticket médio" value={formatCurrency(totals.avg_ticket || 0)} color="#0ea5e9" icon={<TrendIcon fontSize="small" />} size="lg" loading={loading} /></Grid>
        <Grid item xs={6} sm={3}><KpiCard title="Conclusão" value={formatPct(totals.completion_rate)} subtitle={`No-show ${formatPct(totals.no_show_rate)}`} color="#22c55e" icon={<DoneIcon fontSize="small" />} size="lg" loading={loading} /></Grid>
      </Grid>

      <ChartCard title="Receita por profissional" subtitle="Ordenado por receita">
        {list.length === 0 ? <EmptyState /> : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={list.slice(0, 12)} layout="vertical" margin={{ top: 10, right: 20, left: 80, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis type="number" tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`} />
              <YAxis type="category" dataKey="professional_name" tick={{ fontSize: 11, fill: '#64748b' }} width={170} />
              <RTooltip {...tooltipStyle} formatter={(v: any) => formatCurrency(v)} />
              <Bar dataKey="revenue" name="Receita" fill="#14b8a6" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      <SectionCard title="Detalhamento por profissional">
        {list.length === 0 ? <EmptyState /> : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Profissional</TableCell>
                  <TableCell>Especialidade</TableCell>
                  <TableCell align="right">Atendimentos</TableCell>
                  <TableCell align="right">Concluídos</TableCell>
                  <TableCell align="right">Faltas</TableCell>
                  <TableCell align="right">Conclusão</TableCell>
                  <TableCell align="right">Ticket médio</TableCell>
                  <TableCell align="right">Receita</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {list.map((p: any) => (
                  <TableRow key={p.professional_id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{p.professional_name}</TableCell>
                    <TableCell>{p.specialty || '—'}</TableCell>
                    <TableCell align="right">{formatNumber(p.total_appointments)}</TableCell>
                    <TableCell align="right">{formatNumber(p.completed_appointments)}</TableCell>
                    <TableCell align="right">{formatNumber(p.no_shows)}</TableCell>
                    <TableCell align="right" sx={{ color: '#15803d', fontWeight: 600 }}>{formatPct(p.completion_rate)}</TableCell>
                    <TableCell align="right">{formatCurrency(p.avg_ticket)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: '#0f766e' }}>{formatCurrency(p.revenue)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </SectionCard>
    </Stack>
  );
};

// ===================================================================
// 7. PATIENTS / DEMOGRAPHICS
// ===================================================================
export const PatientsSection: React.FC<{ data: any; loading?: boolean }> = ({ data, loading }) => {
  const s = data?.summary || {};
  const monthly = data?.monthly_new || [];
  const byGender = data?.by_gender || [];
  const byAge = data?.by_age_group || [];
  const byCity = data?.by_city || [];
  const bySource = data?.by_source || [];

  return (
    <Stack spacing={2}>
      <Grid container spacing={2}>
        <Grid item xs={6} sm={3}><KpiCard title="Total" value={formatNumber(s.total || 0)} color="#3b82f6" icon={<PeopleIcon fontSize="small" />} size="lg" loading={loading} /></Grid>
        <Grid item xs={6} sm={3}><KpiCard title="Ativos" value={formatNumber(s.active || 0)} color="#22c55e" icon={<DoneIcon fontSize="small" />} size="lg" loading={loading} /></Grid>
        <Grid item xs={6} sm={3}><KpiCard title="Novos no período" value={formatNumber(s.new_in_period || 0)} color="#0ea5e9" icon={<TrendIcon fontSize="small" />} size="lg" loading={loading} /></Grid>
        <Grid item xs={6} sm={3}><KpiCard title="Com convênio" value={formatNumber(s.with_insurance || 0)} subtitle={`${formatNumber(s.without_insurance || 0)} particulares`} color="#a855f7" icon={<PeopleIcon fontSize="small" />} size="lg" loading={loading} /></Grid>
      </Grid>

      <ChartCard title="Novos pacientes por mês" subtitle="Cadastros realizados no período">
        {monthly.length === 0 ? <EmptyState /> : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={monthly} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748b' }} />
              <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
              <RTooltip {...tooltipStyle} />
              <Area type="monotone" dataKey="count" name="Novos" stroke="#3b82f6" fill="#3b82f624" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <ChartCard title="Por gênero" height={280}>
            {byGender.length === 0 ? <EmptyState /> : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={byGender} dataKey="count" nameKey="gender" outerRadius={90} label labelLine={false}>
                    {byGender.map((_: any, i: number) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <RTooltip {...tooltipStyle} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <ChartCard title="Por faixa etária" height={280}>
            {byAge.length === 0 ? <EmptyState /> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={byAge} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="group" tick={{ fontSize: 11, fill: '#64748b' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
                  <RTooltip {...tooltipStyle} />
                  <Bar dataKey="count" name="Pacientes" fill="#0ea5e9" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <ChartCard title="Top cidades" height={300}>
            {byCity.length === 0 ? <EmptyState /> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={byCity.slice(0, 12)} layout="vertical" margin={{ top: 10, right: 10, left: 80, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" tick={{ fontSize: 11, fill: '#64748b' }} />
                  <YAxis type="category" dataKey="city" tick={{ fontSize: 11, fill: '#64748b' }} width={140} />
                  <RTooltip {...tooltipStyle} />
                  <Bar dataKey="count" fill="#8b5cf6" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <ChartCard title="Origens dos pacientes" height={300}>
            {bySource.length === 0 ? <EmptyState /> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={bySource.slice(0, 12)} layout="vertical" margin={{ top: 10, right: 10, left: 80, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" tick={{ fontSize: 11, fill: '#64748b' }} />
                  <YAxis type="category" dataKey="source" tick={{ fontSize: 11, fill: '#64748b' }} width={140} />
                  <RTooltip {...tooltipStyle} />
                  <Bar dataKey="count" fill="#f59e0b" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Grid>
      </Grid>
    </Stack>
  );
};

// ===================================================================
// 8. PROCEDURES
// ===================================================================
export const ProceduresSection: React.FC<{ data: any; loading?: boolean }> = ({ data, loading }) => {
  const s = data?.summary || {};
  const top = data?.top_procedures || [];

  return (
    <Stack spacing={2}>
      <Grid container spacing={2}>
        <Grid item xs={6} sm={3}><KpiCard title="Receita orçada" value={formatCurrency(s.total_budgeted_revenue || 0)} color="#14b8a6" icon={<MoneyIcon fontSize="small" />} size="lg" loading={loading} /></Grid>
        <Grid item xs={6} sm={3}><KpiCard title="Qtd. orçada" value={formatNumber(s.total_budgeted_qty || 0)} color="#0ea5e9" icon={<ReceiptIcon fontSize="small" />} size="lg" loading={loading} /></Grid>
        <Grid item xs={6} sm={3}><KpiCard title="Qtd. executada" value={formatNumber(s.total_executed_qty || 0)} color="#22c55e" icon={<DoneIcon fontSize="small" />} size="lg" loading={loading} /></Grid>
        <Grid item xs={6} sm={3}><KpiCard title="Conversão" value={formatPct(s.conversion_rate)} subtitle="orçado → executado" color="#a855f7" icon={<TrendIcon fontSize="small" />} size="lg" loading={loading} /></Grid>
      </Grid>

      <ChartCard title="Top procedimentos por receita orçada">
        {top.length === 0 ? <EmptyState /> : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={top.slice(0, 12)} layout="vertical" margin={{ top: 10, right: 10, left: 100, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis type="number" tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#64748b' }} width={200} />
              <RTooltip {...tooltipStyle} formatter={(v: any) => formatCurrency(v)} />
              <Bar dataKey="budgeted_revenue" name="Receita orçada" fill="#14b8a6" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      <SectionCard title="Detalhamento de procedimentos">
        {top.length === 0 ? <EmptyState /> : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Procedimento</TableCell>
                  <TableCell align="right">Orçados</TableCell>
                  <TableCell align="right">Executados</TableCell>
                  <TableCell align="right">Conversão</TableCell>
                  <TableCell align="right">Receita orçada</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {top.map((p: any) => (
                  <TableRow key={p.name} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{p.name}</TableCell>
                    <TableCell align="right">{formatNumber(p.budgeted_qty)}</TableCell>
                    <TableCell align="right">{formatNumber(p.executed_qty)}</TableCell>
                    <TableCell align="right">{formatPct(p.conversion_rate)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: '#0f766e' }}>{formatCurrency(p.budgeted_revenue)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </SectionCard>
    </Stack>
  );
};

// ===================================================================
// 9. BUDGETS
// ===================================================================
export const BudgetsSection: React.FC<{ data: any; loading?: boolean }> = ({ data, loading }) => {
  const s = data?.summary || {};
  const byStatus = data?.by_status || [];
  const monthly = data?.monthly || [];

  return (
    <Stack spacing={2}>
      <Grid container spacing={2}>
        <Grid item xs={6} sm={3}><KpiCard title="Orçamentos" value={formatNumber(s.total || 0)} color="#a855f7" icon={<ReceiptIcon fontSize="small" />} size="lg" loading={loading} /></Grid>
        <Grid item xs={6} sm={3}><KpiCard title="Valor total" value={formatCurrency(s.total_amount || 0)} color="#14b8a6" icon={<MoneyIcon fontSize="small" />} size="lg" loading={loading} /></Grid>
        <Grid item xs={6} sm={3}><KpiCard title="Aprovados" value={formatNumber(s.approved_count || 0)} subtitle={formatCurrency(s.approved_amount || 0)} color="#22c55e" icon={<DoneIcon fontSize="small" />} size="lg" loading={loading} /></Grid>
        <Grid item xs={6} sm={3}><KpiCard title="Conversão" value={formatPct(s.conversion_rate)} subtitle={`Ticket ${formatCurrency(s.avg_ticket || 0)}`} color="#0ea5e9" icon={<TrendIcon fontSize="small" />} size="lg" loading={loading} /></Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <ChartCard title="Por status" height={320}>
            {byStatus.length === 0 ? <EmptyState /> : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={byStatus} dataKey="amount" nameKey="status_label" outerRadius={100} label={(e: any) => `${e.status_label} (${e.count})`} labelLine={false}>
                    {byStatus.map((_: any, i: number) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <RTooltip {...tooltipStyle} formatter={(v: any) => formatCurrency(v)} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={7}>
          <ChartCard title="Volume mensal" subtitle="Total emitido x aprovados" height={320}>
            {monthly.length === 0 ? <EmptyState /> : (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={monthly} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748b' }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#64748b' }} />
                  <RTooltip {...tooltipStyle} formatter={(v: any, name: any) => name === 'Valor' ? formatCurrency(v) : v} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar yAxisId="left" dataKey="amount" name="Valor" fill="#a855f7" radius={[6, 6, 0, 0]} />
                  <Line yAxisId="right" type="monotone" dataKey="approved" name="Aprovados" stroke="#22c55e" strokeWidth={2.5} />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Grid>
      </Grid>
    </Stack>
  );
};

// ===================================================================
// 10. COMMISSIONS
// ===================================================================
export const CommissionsSection: React.FC<{ data: any; loading?: boolean }> = ({ data, loading }) => {
  const s = data?.summary || {};
  const list = data?.by_professional || [];
  if (data?.unavailable) {
    return <EmptyState title="Módulo de comissões indisponível na sua instalação" />;
  }
  return (
    <Stack spacing={2}>
      <Grid container spacing={2}>
        <Grid item xs={6} sm={3}><KpiCard title="Total" value={formatCurrency(s.total_amount || 0)} subtitle={`${formatNumber(s.count || 0)} lançamentos`} color="#d946ef" icon={<MoneyIcon fontSize="small" />} size="lg" loading={loading} /></Grid>
        <Grid item xs={6} sm={3}><KpiCard title="Pago" value={formatCurrency(s.total_paid || 0)} color="#22c55e" icon={<DoneIcon fontSize="small" />} size="lg" loading={loading} /></Grid>
        <Grid item xs={6} sm={3}><KpiCard title="Pendente" value={formatCurrency(s.total_pending || 0)} color="#f59e0b" icon={<PaymentsIcon fontSize="small" />} size="lg" loading={loading} /></Grid>
      </Grid>
      <ChartCard title="Comissão por profissional">
        {list.length === 0 ? <EmptyState /> : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={list.slice(0, 15)} layout="vertical" margin={{ top: 10, right: 10, left: 100, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis type="number" tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`} />
              <YAxis type="category" dataKey="professional_name" tick={{ fontSize: 11, fill: '#64748b' }} width={170} />
              <RTooltip {...tooltipStyle} formatter={(v: any) => formatCurrency(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="paid" name="Pago" stackId="a" fill="#22c55e" radius={[0, 0, 0, 0]} />
              <Bar dataKey="pending" name="Pendente" stackId="a" fill="#f59e0b" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartCard>
      <SectionCard title="Detalhe">
        {list.length === 0 ? <EmptyState /> : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow><TableCell>Profissional</TableCell><TableCell align="right">Lançamentos</TableCell><TableCell align="right">Pago</TableCell><TableCell align="right">Pendente</TableCell><TableCell align="right">Total</TableCell></TableRow>
              </TableHead>
              <TableBody>
                {list.map((p: any) => (
                  <TableRow key={p.professional_id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{p.professional_name}</TableCell>
                    <TableCell align="right">{formatNumber(p.count)}</TableCell>
                    <TableCell align="right" sx={{ color: '#15803d' }}>{formatCurrency(p.paid)}</TableCell>
                    <TableCell align="right" sx={{ color: '#b45309' }}>{formatCurrency(p.pending)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrency(p.total)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </SectionCard>
    </Stack>
  );
};

// ===================================================================
// 11. INVENTORY
// ===================================================================
export const InventorySection: React.FC<{ data: any; loading?: boolean }> = ({ data, loading }) => {
  const s = data?.summary || {};
  const low = data?.low_stock || [];
  const top = data?.top_consumed || [];
  if (data?.unavailable) return <EmptyState title="Módulo de estoque indisponível" />;
  return (
    <Stack spacing={2}>
      <Grid container spacing={2}>
        <Grid item xs={6} sm={3}><KpiCard title="Itens cadastrados" value={formatNumber(s.total_items || 0)} color="#06b6d4" icon={<InventoryIcon fontSize="small" />} size="lg" loading={loading} /></Grid>
        <Grid item xs={6} sm={3}><KpiCard title="Estoque baixo" value={formatNumber(s.low_stock_items || 0)} subtitle={`${formatNumber(s.ruptured_items || 0)} em ruptura`} color="#ef4444" icon={<WarningIcon fontSize="small" />} size="lg" loading={loading} /></Grid>
        <Grid item xs={6} sm={3}><KpiCard title="Entradas" value={formatNumber(s.total_in_qty || 0)} subtitle={formatCurrency(s.total_in_value || 0)} color="#22c55e" icon={<TrendIcon fontSize="small" />} size="lg" loading={loading} /></Grid>
        <Grid item xs={6} sm={3}><KpiCard title="Saídas" value={formatNumber(s.total_out_qty || 0)} subtitle={formatCurrency(s.total_out_value || 0)} color="#f59e0b" icon={<TrendIcon fontSize="small" />} size="lg" loading={loading} /></Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <ChartCard title="Top consumidos no período">
            {top.length === 0 ? <EmptyState /> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={top.slice(0, 12)} layout="vertical" margin={{ top: 10, right: 10, left: 100, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" tick={{ fontSize: 11, fill: '#64748b' }} />
                  <YAxis type="category" dataKey="item_name" tick={{ fontSize: 11, fill: '#64748b' }} width={180} />
                  <RTooltip {...tooltipStyle} />
                  <Bar dataKey="quantity" name="Qtd" fill="#06b6d4" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={5}>
          <SectionCard title="Itens em estoque baixo" subtitle="Atenção: reposição">
            {low.length === 0 ? <EmptyState title="Sem alertas de baixo estoque" /> : (
              <TableContainer sx={{ maxHeight: 320 }}>
                <Table size="small" stickyHeader>
                  <TableHead><TableRow><TableCell>Item</TableCell><TableCell align="right">Atual</TableCell><TableCell align="right">Mín.</TableCell></TableRow></TableHead>
                  <TableBody>
                    {low.map((i: any) => (
                      <TableRow key={i.id} hover>
                        <TableCell>{i.name}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: i.current_stock <= 0 ? '#b91c1c' : '#b45309' }}>{formatNumber(i.current_stock)}</TableCell>
                        <TableCell align="right">{formatNumber(i.minimum_stock)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </SectionCard>
        </Grid>
      </Grid>
    </Stack>
  );
};

// ===================================================================
// 12. INSURANCE
// ===================================================================
export const InsuranceSection: React.FC<{ data: any; loading?: boolean }> = ({ data, loading }) => {
  const s = data?.summary || {};
  const list = data?.by_insurance || [];
  return (
    <Stack spacing={2}>
      <Grid container spacing={2}>
        <Grid item xs={6} sm={3}><KpiCard title="Convênios" value={formatNumber(s.total_insurances || 0)} color="#f43f5e" icon={<ReceiptIcon fontSize="small" />} size="lg" loading={loading} /></Grid>
        <Grid item xs={6} sm={3}><KpiCard title="Com convênio" value={formatNumber(s.patients_with_insurance || 0)} color="#a855f7" icon={<PeopleIcon fontSize="small" />} size="lg" loading={loading} /></Grid>
        <Grid item xs={6} sm={3}><KpiCard title="Particulares" value={formatNumber(s.patients_private || 0)} color="#0ea5e9" icon={<PeopleIcon fontSize="small" />} size="lg" loading={loading} /></Grid>
        <Grid item xs={6} sm={3}><KpiCard title="Receita total" value={formatCurrency(s.revenue_total || 0)} color="#14b8a6" icon={<MoneyIcon fontSize="small" />} size="lg" loading={loading} /></Grid>
      </Grid>
      <ChartCard title="Receita por convênio">
        {list.length === 0 ? <EmptyState /> : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={list.slice(0, 15)} layout="vertical" margin={{ top: 10, right: 10, left: 100, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis type="number" tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#64748b' }} width={170} />
              <RTooltip {...tooltipStyle} formatter={(v: any) => formatCurrency(v)} />
              <Bar dataKey="revenue" name="Receita" fill="#f43f5e" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartCard>
      <SectionCard title="Detalhamento">
        {list.length === 0 ? <EmptyState /> : (
          <TableContainer>
            <Table size="small">
              <TableHead><TableRow><TableCell>Convênio</TableCell><TableCell align="right">Pacientes</TableCell><TableCell align="right">Receita</TableCell></TableRow></TableHead>
              <TableBody>
                {list.map((r: any) => (
                  <TableRow key={r.name} hover>
                    <TableCell>{r.name}</TableCell>
                    <TableCell align="right">{formatNumber(r.patients)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: '#0f766e' }}>{formatCurrency(r.revenue)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </SectionCard>
    </Stack>
  );
};

export const LoadingSection: React.FC = () => (
  <Stack spacing={2}>
    <Grid container spacing={2}>
      {[0, 1, 2, 3].map(i => (
        <Grid key={i} item xs={6} sm={3}><Skeleton variant="rounded" height={110} /></Grid>
      ))}
    </Grid>
    <Skeleton variant="rounded" height={320} />
    <Skeleton variant="rounded" height={300} />
  </Stack>
);
